import {Component, Inject, OnInit} from '@angular/core';

import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';

import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

import {Entry, Group, ParticipantMember, Schedule, Timezone} from 'src/app/models';
import {GroupsService, IndividualsService, SchedulesService, SnackbarService} from 'src/app/services';

import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
    selector: 'app-schedule-create',
    templateUrl: './schedule-create.component.html',
    styleUrls: ['./schedule-create.component.scss', '../schedule-shared.scss']
})
export class ScheduleCreateComponent implements OnInit {
    participantTypes = ['Group', 'Individual or Channel'];
    repeatTypes = ['Weekly', 'Daily'];
    daysOfWeek = [
        {day: 'Mon', id: 2, select: false},
        {day: 'Tue', id: 3, select: false},
        {day: 'Wed', id: 4, select: false},
        {day: 'Thurs', id: 5, select: false},
        {day: 'Fri', id: 6, select: false},
        {day: 'Sat', id: 7, select: false},
        {day: 'Sun', id: 1, select: false}
    ];
    currentGroup: Group;
    participantType = 'Individual or Channel';
    individualChange = new Subject<string>();
    groupChange = new Subject<string>();
    autocompleteIndOrDevice = [];
    autocompleteGroups = [];
    individualsAndDevices: any;
    groups: any;
    participantList = [];
    participantInput = '';
    removable = true;
    scheduleType = 'Weekly';
    startDate = '';
    endDate = '';
    entryName;
    currentSched = new Schedule();
    closeDialog = false;
    ordinalLength = 0;
    hourglass = false;
    timezone: Timezone;
    disabled: boolean;
    days_of_week = [
        {
            'id': 2,
            'name': 'MONDAY'
        },
        {
            'id': 3,
            'name': 'TUESDAY'
        },
        {
            'id': 4,
            'name': 'WEDNESDAY'
        },
        {
            'id': 5,
            'name': 'THURSDAY'
        },
        {
            'id': 6,
            'name': 'FRIDAY'
        },
        {
            'id': 7,
            'name': 'SATURDAY'
        },
        {
            'id': 1,
            'name': 'SUNDAY'
        }
    ];

    intlForHTML = Intl;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private individualsService: IndividualsService,
        private groupsService: GroupsService,
        private scheduleService: SchedulesService,
        private snackbarService: SnackbarService,
        public dialogRef: MatDialogRef<ScheduleCreateComponent>) {
        this.currentGroup = this.data.dataKey;
        this.currentSched.entries = this.data.entriesList;
        this.ordinalLength = this.currentSched.entries ? this.currentSched.entries.length || 0 : 0;
        this.entryName = this.currentGroup.name + ' entry ' + (this.currentSched.entries ? this.ordinalLength : '');
        this.timezone = this.data.currentTimezone;
        this.disabled = this.data.disableTimezone;
    }

    ngOnInit() {
        this.setupOnChangeSearches();
        if (this.data.override === true) {
            this.scheduleType = 'Override';
        }
    }

    setupOnChangeSearches() {
        const delay = 750;
        this.individualChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getIndividualsAndDevices(term));

        this.groupChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getGroupNames(term));
    }

    handleTimezoneChange(newTimezone: Timezone) {
        if (this.startDate) {
            this.startDate = dayjs.tz(this.startDate, this.timezone.id).tz(newTimezone.id).format('YYYY-MM-DDTHH:mm');
        }
        if (this.endDate) {
            this.endDate = dayjs.tz(this.endDate, this.timezone.id).tz(newTimezone.id).format('YYYY-MM-DDTHH:mm');
        }
        this.timezone = newTimezone;
    }

    getIndividualsAndDevices(filterValueName: string) {
        if (filterValueName != null && filterValueName.length > 1) {
            this.individualsService.getIndividualsAndDevices(filterValueName).subscribe(individAndDevices => {
                this.individualsAndDevices = individAndDevices.content;
                this.autocompleteIndOrDevice = [];
                this.individualsAndDevices.forEach(e => {
                    this.autocompleteIndOrDevice.push(e);
                });
            });
        } else {
            this.individualsAndDevices = [];
        }
    }

    getGroupNames(filterValueGroup: string) {
        if (filterValueGroup != null && filterValueGroup.length > 1) {
            this.groupsService.getGroupNames(filterValueGroup).subscribe(groups => {
                this.individualsAndDevices = groups;
                this.autocompleteIndOrDevice = [];
                this.individualsAndDevices.forEach(e => {
                    if (!((this.currentGroup.name === e.name) && !!e.group_type)) {
                        this.autocompleteIndOrDevice.push(e);
                    }
                });
            });
        } else {
            this.individualsAndDevices = [];
        }
    }

    indChange($event) {
        this.individualChange.next($event);
    }

    grpChange($event) {
        this.groupChange.next($event);
    }

    setupParticipant(event: any): any {
        const newParticipant: any = new ParticipantMember();
        newParticipant.active = false;
        newParticipant.duration = this.scheduleType.toLowerCase() === 'daily' ? 24 : 168;
        newParticipant.ordinal = this.participantList.length === 0 ? 0 : this.participantList[this.participantList.length - 1].ordinal + 1;
        newParticipant.participant_id = event.id;
        newParticipant.participant_name = (event.full_name ? event.full_name : event.name);
        newParticipant.participant_type = (event.full_name ? 'INDIVIDUAL' : (event.group_type ? 'GROUP' : 'DEVICE'));
        if (this.currentGroup.id === event.id && !!event.group_type) {
            this.snackbarService.openSnackBar('Cannot add ' + newParticipant.participant_name + ' to it\'s own schedule', true);
        } else {
            this.participantList.push(newParticipant);
        }
        this.participantInput = '';
    }

    isGroup() {
        return this.participantType === 'Group';
    }

    removeParticipant(participant) {
        this.participantList = this.participantList.filter(p => p !== participant);
    }

    save() {
        if (this.testValidity()) {
            this.hourglass = true;
            const schEntry = new Entry(); // refactor for sched->entry
            schEntry.group_id = this.currentGroup.id;
            schEntry.group_name = this.currentGroup.name;
            schEntry.entry_name = this.entryName === '' ? this.currentGroup.name + ' Schedule ' + (this.ordinalLength + 1) : this.entryName;
            schEntry.ordinal = this.ordinalLength;
            schEntry.timezone = this.timezone.id;
            schEntry.starts = dayjs.tz(this.startDate, this.timezone.id).utc().valueOf();
            schEntry.ends = !this.endDate ? null : dayjs.tz(this.endDate, this.timezone.id).utc().valueOf();
            if (this.scheduleType === 'Weekly') {
                schEntry.frequency_id = 2;
                schEntry.frequency_name = 'weekly';
            } else {
                schEntry.frequency_id = 1;
                schEntry.frequency_name = 'daily';
                this.daysOfWeek.forEach(day => {
                    day.select = true;
                });
            }
            const selectDays = this.daysOfWeek.filter(d => d.select === true);
            const dayArray = [];
            selectDays.forEach(d => {
                const day = this.days_of_week.find(di => di.id === d.id);
                dayArray.push(day);
            });
            schEntry.days_of_week = dayArray;
            schEntry.participants = this.participantList;
            if (this.data.entryIdsToRemove) {
                this.data.entryIdsToRemove.forEach((id) => this.scheduleService.deleteScheduleEntry(id).subscribe());
            }
            if (this.ordinalLength === 0) {
                this.scheduleService.createSchedule([schEntry]).subscribe({
                    next: () => this.createOrUpdateAfterSuccess(),
                    error: error => this.createOrUpdateAfterError(error)
                });
            } else {
                this.currentSched.entries.push(schEntry);
                this.currentSched.entries.forEach((entry) => entry.timezone = this.timezone.id); // force same timezone
                this.scheduleService.updateSchedule(this.currentSched.entries).subscribe({
                    next: () => this.createOrUpdateAfterSuccess(),
                    error: error => this.createOrUpdateAfterError(error)
                });
            }
        }
    }

    createOrUpdateAfterSuccess() {
        this.snackbarService.openSnackBar('Entry Created Successfully', false);
        this.hourglass = false;
        this.dialogRef.close(true);
    }

    createOrUpdateAfterError(error) {
        this.hourglass = false;
        this.currentSched.entries.pop();
        this.snackbarService.openSnackBar(error.error.message + ' : Could not create entry.', true);
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.participantList, event.previousIndex, event.currentIndex);
    }

    /**
     * When focus is on Duration input, stop event propagation to the parent mat-chip.
     */
    onChangeDuration(event: KeyboardEvent) {
        event.stopPropagation();
    }

    testValidity(): boolean {
        const selectDays = this.daysOfWeek.filter(d => d.select === true);
        if (!this.entryName) {
            this.snackbarService.openSnackBar('Please define a name for this entry and try again.', true);
            return false;
        } else if (!this.startDate || this.startDate == "Invalid Date") {
            this.snackbarService.openSnackBar('Please define a start date & time and try again.', true);
            return false;
        } else if (this.endDate == "Invalid Date") {
            this.snackbarService.openSnackBar('Please define an end date & time and try again.', true);
            return false;
        } else if (this.verifyDates()) {
            this.snackbarService.openSnackBar('Please make sure the start date is before the end date and try again.', true);
            return false;
        } else if ((this.scheduleType === 'Weekly') && (!(selectDays.length > 0))) {
            this.snackbarService.openSnackBar('Please define the days of the week for this entry and try again.', true);
            return false;
        } else if (!(this.participantList.length > 0)) {
            this.snackbarService.openSnackBar('Please define the participants assigned to this entry and try again.', true);
            return false;
        } else {
            for (let j = 0; j < this.participantList.length; j++) {
                const participant = this.participantList[j];
                if (participant.duration === undefined || participant.duration % .25 !== 0 || participant.duration < .25) {
                    this.snackbarService.openSnackBar('Please set the duration to a positive number divisible by .25 for participant ' + participant.participant_name + ' in ' + this.entryName + '.', true);
                    return false;
                }
            }
        }
        return true;
    }

    makeEvenMultiple(num: number): number {
        const justDecimal = (Math.round((num % 1) * 100)) / 100;
        return justDecimal === .7 ? num - (justDecimal) + .75 : (justDecimal === .2 ? num - (justDecimal) + .25 : num - (num % .25));
    }

    // Test If Start Date Is Before End Date
    // Note: this does NOT test if there is a start date.  That is done in the testValidity() function.
    verifyDates() {
        if (this.startDate == null || this.endDate == null) {
            // Since There Is Not a Start or End Date, We Do Not Need To Test That THe Start Dates Comes First
            return true;
        }
        return new Date(this.startDate) > new Date(this.endDate);
    }
}
